import { Playground } from '../../../components/playground/Playground';
import {
    PlaygroundClasses,
    PlaygroundResponsiveClasses,
    PlaygroundPrintClasses,
    PlaygroundClassesSpacer,
} from '../../../components/playground/PlaygroundClasses';
import { Description } from '../../../components/Description';
import { Headline } from '../../../components/Headline';

import { sizes } from '../../../utils/sizes';

export const Width = () => (
    <div>
        <Headline anchor='width'>Width</Headline>
        <Playground example={widthExample} classes={widthClasses} />
        <Headline anchor='min-width'>Content based min-width</Headline>
        <Description>
            <p className='margin-bottom-10'>
                The <code>min-width</code> property defines the smallest width an element can shrink to. When using
                keywords like <code>max-content</code>, <code>min-content</code>, or
                <code>fit-content</code>, you tell the browser how the element should behave in relation to its content
                size. These values are especially useful in responsive layouts or flexible components where
                content-driven sizing is required.
            </p>

            <ul className='space-y-10 margin-bottom-20'>
                <li>
                    <code>min-width-min-content</code>
                    <br />
                    The element’s minimum width becomes the smallest possible width without causing content overflow. It
                    allows text wrapping, meaning the element will compress as much as possible while still displaying
                    all content correctly.
                </li>

                <li>
                    <code>min-width-max-content</code>
                    <br />
                    The element’s minimum width equals the longest unbreakable line of its content. It prevents text or
                    inline items from wrapping, forcing the box to expand horizontally to fit the entire content in one
                    line. This may cause overflow if the content is too wide for its container.
                </li>

                <li>
                    <code>min-width-fit-content</code>
                    <br />
                    This is a hybrid behavior — the element adapts its width somewhere between
                    <code>min-content</code> and <code>max-content</code>. The browser decides the best fit for the
                    available space, making it suitable for adaptive or flexible layouts.
                </li>
            </ul>
            <div className='table-responsive'>
                <table className='table table-head-filled table-condensed table-rounded'>
                    <thead>
                        <tr>
                            <th>Property</th>
                            <th>Description</th>
                            <th>Allows wrapping</th>
                            <th>Typical use</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>min-content</td>
                            <td>Shrinks to the smallest possible width without overflowing.</td>
                            <td>
                                <span className='uikit-icon icon-ok uikit-icon-filled text-color-success uikit-icon-align-baseline margin-right-3' />{' '}
                                Yes
                            </td>
                            <td>Compress content safely</td>
                        </tr>
                        <tr>
                            <td>max-content</td>
                            <td>Expands to fit all content on a single line without wrapping.</td>
                            <td>
                                <span className='uikit-icon icon-remove uikit-icon-filled text-color-danger uikit-icon-align-baseline margin-right-3' />{' '}
                                No
                            </td>
                            <td>Prevent text breaking or wrapping</td>
                        </tr>
                        <tr>
                            <td>fit-content</td>
                            <td>
                                Balances between <code>min-content</code> and <code>max-content</code>; adapts
                                naturally.
                            </td>
                            <td>
                                <span className='uikit-icon icon-ok uikit-icon-filled text-color-success uikit-icon-align-baseline margin-right-3' />{' '}
                                Yes
                            </td>
                            <td>Adaptive layouts</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </Description>
        <Playground example={minWidthExample} classes={widthClasses} />
    </div>
);

const widthExample = (
    <>
        <div className='display-flex flex-wrap gap-20 gap-5-xs margin-bottom-25'>
            {sizes.px_0_to_100.map((size, index) => (
                <div className={`bg-info rounded height-20 text-center width-${size}`} key={index}>
                    {Number(size) >= 30 ? `${size}px` : ''}
                </div>
            ))}
        </div>
        <div className='display-grid grid-cols-1 gap-5'>
            {sizes.pct.map((size, index) => (
                <div className={`bg-info rounded padding-5 width-${size}pct`} key={index}>
                    {size}pct
                </div>
            ))}
        </div>
    </>
);

const minWidthExample = (
    <div
        className='display-flex flex-column-xs gap-20 bg-lightest padding-20 rounded overflow-hidden'
        style={{ maxWidth: '520px' }}
    >
        {/* Max-content box */}
        <div className='bg-info padding-10 rounded min-width-max-content border'>
            <b>max-content</b>
            <p className='max-width-150'>This_is_a_really_long_word_that_will_not_break.</p>
        </div>

        {/* Min-content box */}
        <div className='bg-info padding-10 rounded min-width-min-content border'>
            <b>min-content</b>
            <p className='max-width-150'>This is a really long text that will break and wrap.</p>
        </div>

        {/* Fit-content box */}
        <div className='bg-info padding-10 rounded min-width-fit-content border'>
            <b>fit-content</b>
            <p className='max-width-150'>This text fits naturally within the box.</p>
        </div>
    </div>
);

const widthClasses = (
    <>
        <PlaygroundClasses label='Resetting' classes={['width-auto', 'min-width-0', 'min-width-auto']} />
        <PlaygroundClassesSpacer label='Pixel values' />
        <PlaygroundClasses label='Width' prefix='width-' classes={sizes.px} />
        <PlaygroundClasses label='Min width' prefix='min-width-' classes={sizes.px_50_to_500} />
        <PlaygroundClasses label='Max width' prefix='max-width-' classes={['none', '0', ...sizes.px_50_to_500]} />
        <PlaygroundClassesSpacer label='Percentage values' />
        <PlaygroundClasses label='Width' prefix='width-' classes={sizes.pct} suffix='pct' />
        <PlaygroundClasses label='Min width' prefix='min-width-' classes={sizes.pct} suffix='pct' />
        <PlaygroundClasses label='Max width' prefix='max-width-' classes={sizes.pct} suffix='pct' />
        <PlaygroundClassesSpacer label='Viewport values' />
        <PlaygroundClasses label='Width' prefix='width-' classes={sizes.vw} suffix='vw' />
        <PlaygroundClasses label='Min width' prefix='min-width-' classes={sizes.vw} suffix='vw' />
        <PlaygroundClasses label='Max width' prefix='max-width-' classes={sizes.vw} suffix='vw' />
        <PlaygroundClassesSpacer label='Content based' />
        <PlaygroundClasses
            label='Min width'
            prefix='min-width-'
            classes={['min-content', 'max-content', 'fit-content']}
        />
        <PlaygroundResponsiveClasses />
        <PlaygroundPrintClasses />
    </>
);
